import { NmLoader } from '../../../components/NmLoader';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SlideUpOverlay } from '../../../components/SlideUpOverlay';
import { WorkoutTypeIconTile } from '../../../components/WorkoutTypeIconTile';
import { isRtlLocale, normalizeLocale } from '../../../i18n/config';
import { workouts, type WorkoutType } from '../../../services/api';
import {
  getWorkoutTypeDisplayName,
  workoutTypesForCreation,
  workoutTypeHasHelp,
  WORKOUT_TYPE_BRICK_ID,
} from '../../../utils/workoutTypeDisplay';

interface Props {
  open: boolean;
  selectedId: number | null;
  onSelect: (item: WorkoutType) => void;
  onClose: () => void;
}

export function WorkoutTypePickerModal({ open, selectedId, onSelect, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const dir = isRtlLocale(normalizeLocale(i18n.language)) ? 'rtl' : 'ltr';
  const [types, setTypes] = useState<WorkoutType[]>([]);
  const [loading, setLoading] = useState(false);
  const [brickHelpOpen, setBrickHelpOpen] = useState(false);

  const sortedTypes = useMemo(() => workoutTypesForCreation(types), [types]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    workouts.getWorkoutTypes()
      .then(res => setTypes(res.data.data?.workoutTypes ?? []))
      .catch(() => setTypes([]))
      .finally(() => setLoading(false));
  }, [open]);

  const handleSelect = (item: WorkoutType) => {
    onSelect(item);
    onClose();
  };

  const resetBrickHelp = useCallback(() => {
    setBrickHelpOpen(false);
  }, []);

  const toggleBrickHelp = (event: React.MouseEvent) => {
    event.stopPropagation();
    setBrickHelpOpen(prev => !prev);
  };

  return (
    <SlideUpOverlay
      open={open}
      onClose={onClose}
      zIndex={4000}
      panelClassName="wf-type-picker__sheet"
      dir={dir}
      aria-label={t('workoutForm.pickWorkoutType')}
      onOpen={resetBrickHelp}
    >
      <div className="mobile-sheet__grab" aria-hidden />
      <div className="wf-type-picker__head">
        <span className="wf-type-picker__title">{t('workoutForm.pickWorkoutType')}</span>
        <button type="button" className="wf-type-picker__close" onClick={onClose} aria-label={t('common.close')}>
          ×
        </button>
      </div>
      {loading ? (
        <p className="wf-type-picker__loading"><NmLoader size="sm" inline label={t('workoutForm.loadingTypes')} /></p>
      ) : (
        <ul className="wf-type-picker__list">
          {sortedTypes.map(item => {
            const label = getWorkoutTypeDisplayName(item, t);
            const showHelp = workoutTypeHasHelp(item);
            const helpExpanded = showHelp && brickHelpOpen;

            return (
              <li key={item.id} className={helpExpanded ? 'wf-type-picker__row--help-open' : undefined}>
                <div className="wf-type-picker__row">
                  <button
                    type="button"
                    className={`wf-type-picker__item${selectedId === item.id ? ' wf-type-picker__item--active' : ''}`}
                    onClick={() => handleSelect(item)}
                  >
                    <WorkoutTypeIconTile
                      typeId={item.id}
                      name={item.english_name || item.name}
                      size="sm"
                    />
                    <span className="wf-type-picker__label">{label}</span>
                  </button>
                  {showHelp ? (
                    <button
                      type="button"
                      className={`wf-type-picker__help-btn${helpExpanded ? ' wf-type-picker__help-btn--open' : ''}`}
                      aria-label={t('workoutForm.type.brick.helpTitle')}
                      aria-expanded={helpExpanded}
                      aria-controls={`wf-type-help-${WORKOUT_TYPE_BRICK_ID}`}
                      onClick={toggleBrickHelp}
                    >
                      i
                    </button>
                  ) : null}
                </div>
                {helpExpanded ? (
                  <div className="wf-type-picker__help-panel" id={`wf-type-help-${WORKOUT_TYPE_BRICK_ID}`}>
                    <p className="wf-type-picker__help-title">{t('workoutForm.type.brick.helpTitle')}</p>
                    <p className="wf-type-picker__help-body">{t('workoutForm.type.brick.helpBody')}</p>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </SlideUpOverlay>
  );
}
